import React, { useState } from 'react';
import { Galleria } from 'primereact/galleria';
import { PanViewer } from 'react-image-pan-zoom-rotate';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

const url = process.env.REACT_APP_S3_BASE_URL;

// PDF.js worker (CDN 사용 - CRA에서 빌드 경로 이슈 회피, 링크 클릭 시 다운로드 방지하고 뷰어에서 렌더링)
// react-pdf v7 → pdfjs-dist v3.x 는 pdf.worker.min.js 사용
if (typeof pdfjs !== 'undefined' && pdfjs.GlobalWorkerOptions) {
  pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;
}

const isPdf = (item) => {
  const name =
    item?.originalFileName ||
    item?.file?.name ||
    (item?.filePath && item.filePath.split('/').pop()) ||
    '';
  return String(name).toLowerCase().endsWith('.pdf');
};

const isVideo = (item) => {
  const name =
    item?.originalFileName ||
    item?.file?.name ||
    (item?.filePath && item.filePath.split('/').pop()) ||
    '';
  const lower = String(name).toLowerCase();
  const mimeType = item?.file?.type || '';
  return (
    mimeType.startsWith('video/') ||
    lower.endsWith('.mp4') ||
    lower.endsWith('.mov') ||
    lower.endsWith('.avi') ||
    lower.endsWith('.mkv') ||
    lower.endsWith('.webm')
  );
};

// 스크롤 = 위/아래 이동, 줌은 우측 버튼으로만 (휠은 줌 아님)
const ImagePanZoomView = ({ image, alt }) => {
  const [dx, setDx] = useState(0);
  const [dy, setDy] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [flip, setFlip] = useState(false);
  // 휠로 위/아래 이동할 때 쓰는 오프셋 (PanViewer는 초기 pandy만 반영하므로 뷰어 전체를 translateY로 이동)
  const [scrollY, setScrollY] = useState(0);

  const resetAll = () => {
    setDx(0);
    setDy(0);
    setZoom(1);
    setRotation(0);
    setFlip(false);
    setScrollY(0);
  };
  const zoomIn = () => setZoom((z) => z + 0.2);
  const zoomOut = () => setZoom((z) => (z >= 1 ? z - 0.2 : z));
  const rotateLeft = () =>
    setRotation((r) => (r === -3 ? 0 : r - 1));
  const flipImage = () => setFlip((f) => !f);
  const onPan = (x, y) => {
    setDx(x);
    setDy(y);
  };

  // 휠 = 위/아래 이동 (줌 아님)
  const onWheel = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setScrollY((y) => y + e.deltaY);
  };

  return (
    <div
      style={{ width: '100%', height: '100%', position: 'relative' }}
      onWheel={onWheel}
      role="presentation"
    >
      <div
        style={{
          position: 'absolute',
          right: 10,
          zIndex: 2,
          top: 10,
          userSelect: 'none',
          borderRadius: 2,
          background: '#fff',
          boxShadow: '0px 2px 6px rgba(53, 67, 93, 0.32)',
        }}
      >
        <div
          onClick={zoomIn}
          style={{
            textAlign: 'center',
            cursor: 'pointer',
            height: 40,
            width: 40,
            borderBottom: '1px solid #ccc',
          }}
        >
          <i className="pi pi-plus" style={{ fontSize: '1.2rem' }} />
        </div>
        <div
          onClick={zoomOut}
          style={{
            textAlign: 'center',
            cursor: 'pointer',
            height: 40,
            width: 40,
            borderBottom: '1px solid #ccc',
          }}
        >
          <i className="pi pi-minus" style={{ fontSize: '1.2rem' }} />
        </div>
        <div
          onClick={rotateLeft}
          style={{
            textAlign: 'center',
            cursor: 'pointer',
            height: 40,
            width: 40,
            borderBottom: '1px solid #ccc',
          }}
        >
          <i className="pi pi-refresh" style={{ fontSize: '1.2rem' }} />
        </div>
        <div
          onClick={flipImage}
          style={{
            textAlign: 'center',
            cursor: 'pointer',
            height: 40,
            width: 40,
            borderBottom: '1px solid #ccc',
          }}
        >
          <i className="pi pi-arrows-h" style={{ fontSize: '1.2rem' }} />
        </div>
        <div
          onClick={resetAll}
          style={{
            textAlign: 'center',
            cursor: 'pointer',
            height: 40,
            width: 40,
          }}
        >
          <i className="pi pi-replay" style={{ fontSize: '1.2rem' }} />
        </div>
      </div>
      <div
        style={{
          transform: `translateY(${scrollY}px)`,
          width: '100%',
          height: '100%',
          transition: 'none',
        }}
      >
        <PanViewer
          style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1,
          }}
          zoom={zoom}
          setZoom={setZoom}
          pandx={dx}
          pandy={dy}
          onPan={onPan}
          rotation={rotation}
        >
          <img
            style={{
              transform: `rotate(${rotation * 90}deg) scaleX(${flip ? -1 : 1})`,
              width: '100%',
            }}
            src={image}
            alt={alt}
          />
        </PanViewer>
      </div>
    </div>
  );
};

const PdfViewerContent = ({ pdfSrc, fileName, regTime }) => {
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const onDocumentLoadSuccess = ({ numPages: pages }) => {
    setNumPages(pages);
    setPageNumber(1);
    setLoading(false);
  };
  const onDocumentLoadError = (e) => {
    setError(e?.message || 'PDF 로드 실패');
    setLoading(false);
  };

  return (
    <div style={{ display: 'block', height: '90vh' }}>
      <ul className="font-bold my-4">
        <li>파일명 : {fileName}</li>
        <li>등록일시 : {regTime?.replace('T', ' ')}</li>
      </ul>
      {error ? (
        <div className="p-3 text-red-600">{error}</div>
      ) : (
        <>
          <div className="flex align-items-center justify-content-center gap-2 mb-2">
            <button
              type="button"
              className="p-button p-button-outlined p-button-sm"
              disabled={!numPages || pageNumber <= 1}
              onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
            >
              이전
            </button>
            <span className="text-sm">
              {loading ? '로딩 중...' : `페이지 ${pageNumber} / ${numPages}`}
            </span>
            <button
              type="button"
              className="p-button p-button-outlined p-button-sm"
              disabled={!numPages || pageNumber >= numPages}
              onClick={() =>
                setPageNumber((p) => Math.min(numPages, p + 1))
              }
            >
              다음
            </button>
          </div>
          <div className="flex justify-content-center overflow-auto">
            <Document
              file={pdfSrc}
              onLoadSuccess={onDocumentLoadSuccess}
              onLoadError={onDocumentLoadError}
              loading={null}
            >
              <Page
                pageNumber={pageNumber}
                width={Math.min(window.innerWidth * 0.85, 800)}
                renderTextLayer={true}
                renderAnnotationLayer={true}
              />
            </Document>
          </div>
        </>
      )}
    </div>
  );
};

const ImagesView = () => {
  const images = React.useMemo(
    () => JSON.parse(sessionStorage.getItem('images')) || [],
    []
  );

  const getFileUrl = (item) => {
    if (item?.file?.preview) return item.file.preview;
    const base = process.env.REACT_APP_FILE_SERVER_URL ?? url;
    const path = (item?.filePath || '').trim();
    // filePath가 없고 base가 이미 전체 URL이면 base만 사용 (일부 API는 file_server에 전체 URL 내려줌)
    if (!path) return base && (base.startsWith('http://') || base.startsWith('https://')) ? base : '';
    // filePath가 이미 절대 URL이면 그대로 사용
    if (path.startsWith('http://') || path.startsWith('https://')) return path;
    const baseNorm = String(base).replace(/\/$/, '');
    const pathPart = path.startsWith('/') ? path : `/${path}`;
    return `${baseNorm}${pathPart}`;
  };

  const itemTemplate = (item) => {
    const fileName = item.file ? item.file.name : item.originalFileName;
    if (isVideo(item)) {
      const videoSrc = getFileUrl(item);
      return (
        <div style={{ display: 'block', height: '90vh' }}>
          <div>
            <ul className="font-bold my-4">
              <li>파일명 : {fileName}</li>
              <li>등록일시 : {item.regTime?.replace('T', ' ')}</li>
            </ul>
          </div>
          <div className="flex justify-content-center">
            <video
              src={videoSrc}
              controls
              style={{ maxHeight: '80vh', maxWidth: '100%' }}
            />
          </div>
        </div>
      );
    }
    if (isPdf(item)) {
      const pdfSrc = getFileUrl(item);
      return (
        <PdfViewerContent
          pdfSrc={pdfSrc}
          fileName={fileName}
          regTime={item.regTime}
        />
      );
    }
    return (
      <div
        style={{ display: 'block', height: '90vh' }}
        onClick={(e) => console.log(e.target)}
      >
        <div>
          <ul className="font-bold my-4">
            <li>파일명 : {fileName}</li>
            <li>등록일시 : {item.regTime?.replace('T', ' ')}</li>
          </ul>
        </div>
        <ImagePanZoomView
          image={getFileUrl(item)}
          alt={fileName}
        />
      </div>
    );
  };

  const thumbnailTemplate = (item) => {
    if (isPdf(item)) {
      return (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '50px',
            minWidth: '50px',
            background: '#f0f0f0',
            borderRadius: '4px',
          }}
        >
          <i className="pi pi-file-pdf" style={{ fontSize: '1.5rem', color: '#d32f2f' }} />
        </div>
      );
    }
    if (isVideo(item)) {
      return (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '50px',
            minWidth: '50px',
            background: '#f0f0f0',
            borderRadius: '4px',
          }}
        >
          <i className="pi pi-video" style={{ fontSize: '1.5rem', color: '#1976d2' }} />
        </div>
      );
    }
    return (
      <img
        src={getFileUrl(item)}
        alt={item.file ? item.file.name : item.originalFileName}
        style={{
          display: 'block',
          height: '50px',
        }}
      />
    );
  };

  if (!images?.length) {
    return (
      <div className="pcr_wrapper p-4">
        <p>보여줄 파일이 없습니다.</p>
      </div>
    );
  }

  return (
    <>
      <div className="pcr_wrapper">
        <Galleria
          value={images}
          showItemNavigators
          circular
          numVisible={10}
          thumbnailsPosition="top"
          item={itemTemplate}
          thumbnail={thumbnailTemplate}
        />
      </div>
    </>
  );
};

export default ImagesView;
